import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useOutlet } from '../contexts/OutletContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { useQZPrint } from '../contexts/QZPrintContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import {
  Alert,
  AlertDescription,
} from '../components/ui/alert';
import { toast } from 'sonner';
import {
  Plus,
  Minus,
  Trash2,
  ShoppingCart,
  CreditCard,
  Banknote,
  QrCode,
  Smartphone,
  Search,
  X,
  Loader2,
  UtensilsCrossed,
  Package,
  Bike,
  ChefHat,
  CheckCircle,
  UserPlus,
  UserCircle,
  Phone,
  Percent,
  Tag,
  AlertCircle,
  LayoutGrid,
  List,
  Printer,
  AlertTriangle,
} from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { Label } from '../components/ui/label';
import { MenuItemSkeleton } from '../components/ui/loading-skeletons';

export default function POS() {
  const { t } = useTranslation();
  const { api } = useAuth();
  const { currentOutlet } = useOutlet();
  const { formatCurrency } = useCurrency();
  const { isConnected: qzConnected, canPrintReceipt, printerConfig: qzPrinterConfig, printReceipt, printKitchen, printBar } = useQZPrint();
  const [categories, setCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [tables, setTables] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [cart, setCart] = useState([]);
  const [orderType, setOrderType] = useState('DINE_IN');
  const [selectedTable, setSelectedTable] = useState(null);
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [orderNotes, setOrderNotes] = useState('');
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [showItemDialog, setShowItemDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [selectedAddOns, setSelectedAddOns] = useState([]);
  const [itemNotes, setItemNotes] = useState('');
  const [itemQty, setItemQty] = useState(1);
  const [paymentMethod, setPaymentMethod] = useState('CASH');
  const [paymentAmount, setPaymentAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [processingOrder, setProcessingOrder] = useState(false);
  
  // Item Type Filter for Multi-Business Support
  const [itemTypeFilter, setItemTypeFilter] = useState('ALL'); // ALL, FOOD, DRINK, RETAIL, SERVICE, TICKET, RENTAL
  
  // Tax Settings
  const [taxSettings, setTaxSettings] = useState({
    tax_enabled: true,
    tax_name: 'PPN',
    tax_rate: 10,
    tax_inclusive: false,
    service_charge_enabled: false,
    service_charge_name: 'Service',
    service_charge_rate: 5,
  });
  
  // Payment Mode Settings
  const [paymentModeSettings, setPaymentModeSettings] = useState({
    dine_in_mode: 'POSTPAID',
    takeaway_mode: 'PREPAID',
    delivery_mode: 'PREPAID',
    allow_override: true,
  });
  const [paymentModeOverride, setPaymentModeOverride] = useState(null);

  // Customer Selection
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showCustomerPopover, setShowCustomerPopover] = useState(false);
  const [customerSearchQuery, setCustomerSearchQuery] = useState('');
  const [searchedCustomers, setSearchedCustomers] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [showCreateCustomer, setShowCreateCustomer] = useState(false);
  const [newCustomerData, setNewCustomerData] = useState({ name: '', phone: '', email: '' });

  // View Mode: 'gallery' or 'list'
  const [viewMode, setViewMode] = useState(() => {
    const saved = localStorage.getItem('pos_view_mode');
    return saved === 'gallery' ? 'gallery' : 'list';
  });
  
  // Save view mode to localStorage
  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    localStorage.setItem('pos_view_mode', mode);
  };

  const fetchData = useCallback(async () => {
    if (!currentOutlet?.id) return;
    setLoading(true);
    try {
      const [catResp, itemsResp, tablesResp, settingsResp, taxResp] = await Promise.all([
        api.get('/categories', { params: { outlet_id: currentOutlet.id } }),
        api.get('/menu-items', { params: { outlet_id: currentOutlet.id } }),
        api.get('/tables', { params: { outlet_id: currentOutlet.id } }),
        api.get('/settings/payment-mode', { params: { outlet_id: currentOutlet.id } }),
        api.get('/settings/tax', { params: { outlet_id: currentOutlet.id } }),
      ]);
      setCategories(catResp.data.filter(c => c.is_active));
      setMenuItems(itemsResp.data.filter(i => i.is_available));
      setTables(tablesResp.data);
      setPaymentModeSettings(settingsResp.data);
      setTaxSettings(taxResp.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [api, currentOutlet?.id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filteredItems = menuItems.filter((item) => {
    const matchesCategory = activeCategory === 'all' || item.category_id === activeCategory;
    const matchesSearch = item.name.toLowerCase().includes(searchQuery.toLowerCase());
    // Filter by item type (multi-business support)
    const itemType = item.item_type || 'FOOD';
    const matchesItemType = itemTypeFilter === 'ALL' || itemType === itemTypeFilter;
    return matchesCategory && matchesSearch && matchesItemType;
  });

  const handleItemClick = (item) => {
    if (item.variants?.length > 0 || item.add_ons?.length > 0) {
      setSelectedItem(item);
      setSelectedVariant(item.variants?.[0] || null);
      setSelectedAddOns([]);
      setItemNotes('');
      setItemQty(1);
      setShowItemDialog(true);
    } else {
      addToCart(item, null, [], '', 1);
    }
  };

  const addToCart = (item, variant, addOns, notes, qty) => {
    const addOnTotal = addOns.reduce((sum, a) => sum + a.price, 0) * qty;
    const unitPrice = item.base_price + (variant?.price_adjustment || 0);
    
    const cartItem = {
      id: crypto.randomUUID(),
      menu_item_id: item.id,
      menu_item_name: item.name,
      quantity: qty,
      unit_price: unitPrice,
      variant_id: variant?.id || null,
      variant_name: variant?.name || null,
      add_on_ids: addOns.map(a => a.id),
      add_on_names: addOns.map(a => a.name),
      add_on_total: addOnTotal,
      notes: notes || null,
      kitchen_station: item.kitchen_station,
      item_type: item.item_type || 'FOOD',  // Include item_type for order
    };

    setCart(prev => [...prev, cartItem]);
    toast.success(`${item.name} added to cart`);
  };

  const handleAddToCartFromDialog = () => {
    if (selectedItem) {
      addToCart(selectedItem, selectedVariant, selectedAddOns, itemNotes, itemQty);
      setShowItemDialog(false);
    }
  };

  const updateCartItemQty = (itemId, delta) => {
    setCart(prev => prev.map(item => {
      if (item.id === itemId) {
        const newQty = Math.max(1, item.quantity + delta);
        const addOnTotal = (item.add_on_total / item.quantity) * newQty;
        return { ...item, quantity: newQty, add_on_total: addOnTotal };
      }
      return item;
    }));
  };

  const removeFromCart = (itemId) => {
    setCart(prev => prev.filter(item => item.id !== itemId));
  };

  const clearCart = () => {
    setCart([]);
    setSelectedTable(null);
    setCustomerName('');
    setCustomerPhone('');
    setDeliveryAddress('');
    setOrderNotes('');
    setPaymentModeOverride(null);
    setSelectedCustomer(null);
  };

  // Customer search
  const searchCustomers = useCallback(async (query) => {
    if (!currentOutlet?.id || !query.trim()) {
      setSearchedCustomers([]);
      return;
    }
    setLoadingCustomers(true);
    try {
      const resp = await api.get('/customers/search', {
        params: { outlet_id: currentOutlet.id, q: query, limit: 5 }
      });
      setSearchedCustomers(resp.data);
    } catch (error) {
      console.error('Customer search failed:', error);
      setSearchedCustomers([]);
    } finally {
      setLoadingCustomers(false);
    }
  }, [api, currentOutlet?.id]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      if (customerSearchQuery.trim()) {
        searchCustomers(customerSearchQuery);
      } else {
        setSearchedCustomers([]);
      }
    }, 300);
    return () => clearTimeout(debounce);
  }, [customerSearchQuery, searchCustomers]);

  const handleSelectCustomer = (customer) => {
    setSelectedCustomer(customer);
    setCustomerName(customer.name);
    setCustomerPhone(customer.phone || '');
    setShowCustomerPopover(false);
    setCustomerSearchQuery('');
    toast.success(`Pelanggan ${customer.name} dipilih`);
  };

  const handleRemoveCustomer = () => {
    setSelectedCustomer(null);
    setCustomerName('');
    setCustomerPhone('');
  };

  const handleCreateCustomer = async () => {
    if (!newCustomerData.name.trim()) {
      toast.error('Nama pelanggan wajib diisi');
      return;
    }
    try {
      const resp = await api.post('/customers', {
        ...newCustomerData,
        outlet_id: currentOutlet.id,
      });
      const newCustomer = resp.data.customer;
      handleSelectCustomer(newCustomer);
      setShowCreateCustomer(false);
      setNewCustomerData({ name: '', phone: '', email: '' });
      toast.success('Pelanggan baru berhasil dibuat');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal membuat pelanggan');
    }
  };

  const subtotal = cart.reduce((sum, item) => sum + (item.unit_price * item.quantity) + item.add_on_total, 0);
  
  // Calculate auto discount from customer tag
  const autoDiscountPercent = selectedCustomer?.max_discount || 0;
  const autoDiscountAmount = autoDiscountPercent > 0 ? subtotal * (autoDiscountPercent / 100) : 0;
  
  // Calculate totals with dynamic tax settings
  const taxableAmount = subtotal - autoDiscountAmount;
  
  // Calculate service charge
  const serviceCharge = taxSettings.service_charge_enabled 
    ? taxableAmount * (taxSettings.service_charge_rate / 100) 
    : 0;
  
  // Calculate tax
  let tax = 0;
  if (taxSettings.tax_enabled) {
    if (taxSettings.tax_inclusive) {
      // Tax inclusive: extract from price
      const baseAmount = (taxableAmount + serviceCharge) / (1 + taxSettings.tax_rate / 100);
      tax = (taxableAmount + serviceCharge) - baseAmount;
    } else {
      // Tax exclusive: add to price
      tax = (taxableAmount + serviceCharge) * (taxSettings.tax_rate / 100);
    }
  }
  
  // Calculate total
  const total = taxSettings.tax_inclusive 
    ? taxableAmount + serviceCharge 
    : taxableAmount + serviceCharge + tax;

  // Determine current payment mode based on order type and settings
  const getCurrentPaymentMode = () => {
    if (paymentModeOverride) return paymentModeOverride;
    
    const modeMap = {
      DINE_IN: paymentModeSettings.dine_in_mode,
      TAKEAWAY: paymentModeSettings.takeaway_mode,
      DELIVERY: paymentModeSettings.delivery_mode,
    };
    return modeMap[orderType] || 'POSTPAID';
  };

  const currentPaymentMode = getCurrentPaymentMode();
  const isPrepaid = currentPaymentMode === 'PREPAID';

  const handleCheckout = () => {
    if (cart.length === 0) {
      toast.error('Cart is empty');
      return;
    }
    if (orderType === 'DINE_IN' && !selectedTable) {
      toast.error('Please select a table for dine-in orders');
      return;
    }
    
    if (isPrepaid) {
      // Prepaid: Show payment dialog first
      setPaymentAmount(total.toString());
      setShowPaymentDialog(true);
    } else {
      // Postpaid: Confirm order directly (payment later)
      confirmOrderPostpaid();
    }
  };

  // Postpaid flow: Create order → Confirm → Send to kitchen (no payment yet)
  const confirmOrderPostpaid = async () => {
    setProcessingOrder(true);
    try {
      const orderData = {
        outlet_id: currentOutlet.id,
        order_type: orderType,
        table_id: orderType === 'DINE_IN' ? selectedTable?.id : null,
        table_number: orderType === 'DINE_IN' ? selectedTable?.number : null,
        customer_id: selectedCustomer?.id || null,
        customer_name: customerName || null,
        customer_phone: customerPhone || null,
        delivery_address: orderType === 'DELIVERY' ? deliveryAddress : null,
        notes: orderNotes || null,
        items: cart,
      };

      const orderResp = await api.post('/orders', orderData, {
        params: { payment_mode_override: 'POSTPAID' },
      });
      const order = orderResp.data;

      // Confirm order - sends to kitchen
      await api.put(`/orders/${order.id}/status`, null, {
        params: { new_status: 'CONFIRMED' },
      });

      // ===== AUTO PRINT KITCHEN TICKETS =====
      if (qzConnected && (qzPrinterConfig.kitchen || qzPrinterConfig.bar)) {
        const orderWithDetails = {
          ...order,
          items: cart.map(item => ({
            ...item,
            name: item.menu_item_name || item.name,
            menu_type: item.menu_type || 'FOOD',
          })),
          table_name: selectedTable?.name || selectedTable?.number?.toString() || null,
          customer_name: customerName,
        };
        
        // Print to kitchen (FOOD items)
        if (qzPrinterConfig.kitchen) {
          try {
            const kitchenResult = await printKitchen(orderWithDetails);
            if (kitchenResult.success) {
              console.log('[POS] Kitchen print success');
            } else {
              console.warn('[POS] Kitchen print skipped:', kitchenResult.reason || kitchenResult.error);
            }
          } catch (kitchenErr) {
            console.error('[POS] Kitchen print error:', kitchenErr);
            toast.warning('Gagal cetak tiket dapur');
          }
        }
        
        // Print to bar (DRINK items)
        if (qzPrinterConfig.bar) {
          try {
            const barResult = await printBar(orderWithDetails);
            if (barResult.success) {
              console.log('[POS] Bar print success');
            } else {
              console.warn('[POS] Bar print skipped:', barResult.reason || barResult.error);
            }
          } catch (barErr) {
            console.error('[POS] Bar print error:', barErr);
            toast.warning('Gagal cetak tiket bar');
          }
        }
      } else {
        console.log('[POS] QZ not connected or kitchen/bar printer not configured, skipping auto-print');
      }
      // ===== END AUTO PRINT =====

      // Show success with auto-discount info if applicable
      if (order.auto_discount) {
        toast.success(
          <div className="flex flex-col">
            <span className="font-bold">Order #{order.order_number} sent to kitchen!</span>
            <span className="text-sm text-green-600">
              ✨ Diskon {order.auto_discount.tag_name} ({order.auto_discount.discount_percent}%) otomatis diterapkan!
            </span>
            <span className="text-sm opacity-80">Hemat {formatCurrency(order.auto_discount.discount_amount)}</span>
          </div>,
          { duration: 5000 }
        );
      } else {
        toast.success(
          <div className="flex flex-col">
            <span className="font-bold">Order #{order.order_number} sent to kitchen!</span>
            <span className="text-sm opacity-80">Payment will be collected later</span>
          </div>
        );
      }
      clearCart();
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const errorMsg = typeof detail === 'string' ? detail : 
                       Array.isArray(detail) ? detail.map(d => d.msg || d).join(', ') :
                       detail?.msg || 'Failed to create order';
      toast.error(errorMsg);
    } finally {
      setProcessingOrder(false);
    }
  };

  // Prepaid flow: Create order → Payment → Confirm
  const processPaymentPrepaid = async () => {
    setProcessingOrder(true);
    try {
      const orderData = {
        outlet_id: currentOutlet.id,
        order_type: orderType,
        table_id: orderType === 'DINE_IN' ? selectedTable?.id : null,
        table_number: orderType === 'DINE_IN' ? selectedTable?.number : null,
        customer_id: selectedCustomer?.id || null,
        customer_name: customerName || null,
        customer_phone: customerPhone || null,
        delivery_address: orderType === 'DELIVERY' ? deliveryAddress : null,
        notes: orderNotes || null,
        items: cart,
      };

      const orderResp = await api.post('/orders', orderData, {
        params: { payment_mode_override: 'PREPAID' },
      });
      const order = orderResp.data;

      // Create payment first (prepaid)
      const paymentResp = await api.post('/payments', {
        order_id: order.id,
        outlet_id: currentOutlet.id,
        method: paymentMethod,
        amount: parseFloat(paymentAmount),
      });

      // Then confirm order - sends to kitchen & triggers stock deduction
      await api.put(`/orders/${order.id}/status`, null, {
        params: { new_status: 'CONFIRMED' },
      });

      // ===== AUTO PRINT RECEIPT (PREPAID = Receipt immediately after payment) =====
      if (qzConnected && canPrintReceipt) {
        const orderForPrint = {
          ...order,
          items: cart.map(item => ({
            ...item,
            name: item.menu_item_name || item.name,
            unit_price: item.price,
          })),
          table_name: selectedTable?.name || selectedTable?.number?.toString() || null,
          customer_name: customerName,
          cashier_name: paymentResp.data?.processed_by_name || null,
          payments: [{
            method: paymentMethod,
            amount: parseFloat(paymentAmount),
          }],
        };
        
        try {
          await printReceipt(orderForPrint, currentOutlet);
          console.log('[POS] Receipt print success');
        } catch (receiptErr) {
          console.error('[POS] Receipt print error:', receiptErr);
          toast.error('Gagal cetak struk: ' + receiptErr.message);
        }
      }
      // ===== END AUTO PRINT RECEIPT =====

      // ===== AUTO PRINT KITCHEN TICKETS =====
      if (qzConnected && (qzPrinterConfig.kitchen || qzPrinterConfig.bar)) {
        const orderWithDetails = {
          ...order,
          items: cart.map(item => ({
            ...item,
            name: item.menu_item_name || item.name,
            menu_type: item.menu_type || 'FOOD',
          })),
          table_name: selectedTable?.name || selectedTable?.number?.toString() || null,
          customer_name: customerName,
        };
        
        // Print to kitchen (FOOD items)
        if (qzPrinterConfig.kitchen) {
          try {
            const kitchenResult = await printKitchen(orderWithDetails);
            if (kitchenResult.success) {
              console.log('[POS] Kitchen print success');
            } else {
              console.warn('[POS] Kitchen print skipped:', kitchenResult.reason || kitchenResult.error);
            }
          } catch (kitchenErr) {
            console.error('[POS] Kitchen print error:', kitchenErr);
            toast.warning('Gagal cetak tiket dapur');
          }
        }
        
        // Print to bar (DRINK items)
        if (qzPrinterConfig.bar) {
          try {
            const barResult = await printBar(orderWithDetails);
            if (barResult.success) {
              console.log('[POS] Bar print success');
            } else {
              console.warn('[POS] Bar print skipped:', barResult.reason || barResult.error);
            }
          } catch (barErr) {
            console.error('[POS] Bar print error:', barErr);
            toast.warning('Gagal cetak tiket bar');
          }
        }
      }
      // ===== END AUTO PRINT KITCHEN =====

      // Show success with auto-discount info if applicable
      if (order.auto_discount) {
        toast.success(
          <div className="flex flex-col">
            <span className="font-bold">Order #{order.order_number} paid & sent to kitchen!</span>
            <span className="text-sm text-green-600">
              ✨ Diskon {order.auto_discount.tag_name} ({order.auto_discount.discount_percent}%) otomatis diterapkan!
            </span>
            <span className="text-sm opacity-80">Hemat {formatCurrency(order.auto_discount.discount_amount)}</span>
          </div>,
          { duration: 5000 }
        );
      } else {
        toast.success(`Order #${order.order_number} paid & sent to kitchen!`);
      }
      setShowPaymentDialog(false);
      clearCart();
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const errorMsg = typeof detail === 'string' ? detail : 
                       Array.isArray(detail) ? detail.map(d => d.msg || d).join(', ') :
                       detail?.msg || 'Failed to process payment';
      toast.error(errorMsg);
    } finally {
      setProcessingOrder(false);
    }
  };

  const availableTables = tables.filter(t => t.status === 'AVAILABLE');

  if (!currentOutlet) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <p className="text-muted-foreground">{t('common.select_outlet')}</p>
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-4rem)] lg:h-screen" data-testid="pos-page">
      {/* Menu Section */}
      <div className="flex-1 flex flex-col overflow-hidden border-r">
        {/* Order Type & Search */}
        <div className="p-4 border-b space-y-3">
          <div className="flex gap-2">
            <Button
              variant={orderType === 'DINE_IN' ? 'default' : 'outline'}
              onClick={() => setOrderType('DINE_IN')}
              className="flex-1"
              data-testid="order-type-dinein"
            >
              <UtensilsCrossed className="h-4 w-4 mr-2" />
              {t('pos.dine_in')}
            </Button>
            <Button
              variant={orderType === 'TAKEAWAY' ? 'default' : 'outline'}
              onClick={() => setOrderType('TAKEAWAY')}
              className="flex-1"
              data-testid="order-type-takeaway"
            >
              <Package className="h-4 w-4 mr-2" />
              {t('pos.takeaway')}
            </Button>
            <Button
              variant={orderType === 'DELIVERY' ? 'default' : 'outline'}
              onClick={() => setOrderType('DELIVERY')}
              className="flex-1"
              data-testid="order-type-delivery"
            >
              <Bike className="h-4 w-4 mr-2" />
              {t('pos.delivery')}
            </Button>
          </div>
          
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={t('pos.search_menu')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="menu-search"
            />
          </div>
        </div>

        {/* Categories */}
        <div className="border-b">
          <ScrollArea className="w-full whitespace-nowrap">
            <div className="flex p-2 gap-2">
              <Button
                variant={activeCategory === 'all' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setActiveCategory('all')}
                data-testid="category-all"
              >
                {t('common.all')}
              </Button>
              {categories.map((cat) => (
                <Button
                  key={cat.id}
                  variant={activeCategory === cat.id ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setActiveCategory(cat.id)}
                  data-testid={`category-${cat.id}`}
                >
                  {cat.name}
                </Button>
              ))}
            </div>
          </ScrollArea>
        </div>

        {/* Item Type Filter (Multi-Business Support) */}
        <div className="border-b bg-muted/20">
          <ScrollArea className="w-full whitespace-nowrap">
            <div className="flex p-2 gap-1">
              <Button
                variant={itemTypeFilter === 'ALL' ? 'default' : 'outline'}
                size="sm"
                className="h-7 text-xs"
                onClick={() => setItemTypeFilter('ALL')}
                data-testid="item-type-all"
              >
                Semua
              </Button>
              <Button
                variant={itemTypeFilter === 'FOOD' ? 'default' : 'outline'}
                size="sm"
                className="h-7 text-xs"
                onClick={() => setItemTypeFilter('FOOD')}
                data-testid="item-type-food"
              >
                🍳 Makanan
              </Button>
              <Button
                variant={itemTypeFilter === 'DRINK' ? 'default' : 'outline'}
                size="sm"
                className="h-7 text-xs"
                onClick={() => setItemTypeFilter('DRINK')}
                data-testid="item-type-drink"
              >
                🥤 Minuman
              </Button>
              <Button
                variant={itemTypeFilter === 'RETAIL' ? 'default' : 'outline'}
                size="sm"
                className="h-7 text-xs"
                onClick={() => setItemTypeFilter('RETAIL')}
                data-testid="item-type-retail"
              >
                🛒 Retail
              </Button>
              <Button
                variant={itemTypeFilter === 'SERVICE' ? 'default' : 'outline'}
                size="sm"
                className="h-7 text-xs"
                onClick={() => setItemTypeFilter('SERVICE')}
                data-testid="item-type-service"
              >
                🔧 Layanan
              </Button>
              <Button
                variant={itemTypeFilter === 'TICKET' ? 'default' : 'outline'}
                size="sm"
                className="h-7 text-xs"
                onClick={() => setItemTypeFilter('TICKET')}
                data-testid="item-type-ticket"
              >
                🎟️ Tiket
              </Button>
            </div>
          </ScrollArea>
        </div>

        {/* View Mode Toggle */}
        <div className="flex items-center justify-end px-4 py-2 border-b bg-muted/30">
          <div className="flex items-center gap-1 bg-background rounded-lg p-1 shadow-sm">
            <Button
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              size="sm"
              className="h-8 px-3"
              onClick={() => handleViewModeChange('list')}
              data-testid="view-mode-list"
            >
              <List className="h-4 w-4 mr-1" />
              {t('pos_view.list')}
            </Button>
            <Button
              variant={viewMode === 'gallery' ? 'default' : 'ghost'}
              size="sm"
              className="h-8 px-3"
              onClick={() => handleViewModeChange('gallery')}
              data-testid="view-mode-gallery"
            >
              <LayoutGrid className="h-4 w-4 mr-1" />
              {t('pos_view.gallery')}
            </Button>
          </div>
        </div>

        {/* Menu Items Grid */}
        <ScrollArea className="flex-1 p-4">
          {loading ? (
            <div className="p-4">
              <MenuItemSkeleton count={8} />
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
              <AlertCircle className="h-12 w-12 mb-2 opacity-50" />
              <p>No menu items found</p>
            </div>
          ) : viewMode === 'gallery' ? (
            /* Gallery View - Image-focused grid */
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3">
              {filteredItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => handleItemClick(item)}
                  className="group relative flex flex-col bg-card hover:bg-accent rounded-xl overflow-hidden border shadow-sm transition-all hover:shadow-md active:scale-[0.98]"
                  data-testid={`menu-item-${item.id}`}
                >
                  {/* Image Container - Square aspect ratio */}
                  <div className="aspect-square w-full bg-muted relative overflow-hidden">
                    {item.image_url && item.image_url.length > 0 ? (
                      <img 
                        src={`${process.env.REACT_APP_BACKEND_URL}${item.image_url}`}
                        alt={item.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                        loading="lazy"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    {/* Placeholder for missing/failed images */}
                    <div 
                      className={`absolute inset-0 flex items-center justify-center bg-gradient-to-br from-primary/10 to-primary/5 ${item.image_url && item.image_url.length > 0 ? 'hidden' : 'flex'}`}
                    >
                      <UtensilsCrossed className="h-10 w-10 text-primary/40" />
                    </div>
                    {/* Variants badge */}
                    {item.variants?.length > 0 && (
                      <div className="absolute top-1 right-1">
                        <Badge variant="secondary" className="text-[10px] px-1.5 py-0 bg-background/90 shadow-sm">
                          {item.variants.length}v
                        </Badge>
                      </div>
                    )}
                  </div>
                  {/* Item Info */}
                  <div className="p-2 text-center">
                    <h3 className="font-medium text-xs line-clamp-2 leading-tight min-h-[2rem]">
                      {item.name}
                    </h3>
                    <p className="font-mono text-sm font-bold text-primary mt-1">
                      {formatCurrency(item.base_price)}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            /* List View - Compact with small thumbnails */
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {filteredItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => handleItemClick(item)}
                  className="pos-item-btn text-left flex gap-2"
                  data-testid={`menu-item-${item.id}`}
                >
                  {/* Thumbnail Image */}
                  {item.image_url && item.image_url.length > 0 ? (
                    <img 
                      src={`${process.env.REACT_APP_BACKEND_URL}${item.image_url}`}
                      alt=""
                      className="w-12 h-12 object-cover rounded flex-shrink-0"
                      loading="lazy"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  ) : null}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-sm line-clamp-2">{item.name}</h3>
                    <p className="font-mono text-primary font-semibold mt-1">
                      {formatCurrency(item.base_price)}
                    </p>
                    {item.variants?.length > 0 && (
                      <Badge variant="outline" className="mt-1 text-xs">
                        {item.variants.length} variants
                      </Badge>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </ScrollArea>
      </div>

      {/* Cart Section */}
      <div className="w-80 lg:w-96 flex flex-col bg-card">
        {/* Table Selection for Dine-in */}
        {orderType === 'DINE_IN' && (
          <div className="p-4 border-b">
            <Select
              value={selectedTable?.id || ''}
              onValueChange={(val) => setSelectedTable(tables.find(t => t.id === val))}
            >
              <SelectTrigger data-testid="table-select">
                <SelectValue placeholder={t('pos.select_table')} />
              </SelectTrigger>
              <SelectContent>
                {availableTables.map((table) => (
                  <SelectItem key={table.id} value={table.id}>
                    {table.number} (Cap: {table.capacity})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Customer Info for Takeaway/Delivery */}
        {orderType !== 'DINE_IN' && (
          <div className="p-4 border-b space-y-2">
            <Input
              placeholder={t('pos.customer_name')}
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              data-testid="customer-name"
            />
            {orderType === 'DELIVERY' && (
              <>
                <Input
                  placeholder={t('pos.customer_phone')}
                  value={customerPhone}
                  onChange={(e) => setCustomerPhone(e.target.value)}
                  data-testid="customer-phone"
                />
                <Textarea
                  placeholder={t('pos.delivery_address')}
                  value={deliveryAddress}
                  onChange={(e) => setDeliveryAddress(e.target.value)}
                  rows={2}
                  data-testid="delivery-address"
                />
              </>
            )}
          </div>
        )}

        {/* Customer Selection */}
        <div className="p-3 border-b">
          {selectedCustomer ? (
            <div className="flex items-center justify-between p-2 rounded-md bg-primary/10 border border-primary/20">
              <div className="flex items-center gap-2 min-w-0">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground text-sm font-bold flex-shrink-0">
                  {selectedCustomer.name.charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <p className="font-medium text-sm truncate">{selectedCustomer.name}</p>
                  {selectedCustomer.phone && (
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      <Phone className="h-3 w-3" />
                      {selectedCustomer.phone}
                    </p>
                  )}
                  {selectedCustomer.max_discount > 0 && (
                    <p className="text-xs text-green-600 font-medium flex items-center gap-1">
                      <Percent className="h-3 w-3" />
                      {t('pos.auto_discount')} {selectedCustomer.max_discount}%
                    </p>
                  )}
                </div>
              </div>
              <Button variant="ghost" size="icon" className="h-7 w-7 flex-shrink-0" onClick={handleRemoveCustomer}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <Popover open={showCustomerPopover} onOpenChange={setShowCustomerPopover}>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full justify-start gap-2" data-testid="add-customer-btn">
                  <UserPlus className="h-4 w-4" />
                  <span className="text-muted-foreground">Tambah Pelanggan (Opsional)</span>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-80 p-0" align="start">
                {!showCreateCustomer ? (
                  <div className="p-3 space-y-3">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Cari nama/telepon..."
                        value={customerSearchQuery}
                        onChange={(e) => setCustomerSearchQuery(e.target.value)}
                        className="pl-9"
                        autoFocus
                        data-testid="customer-search-input"
                      />
                    </div>
                    
                    {loadingCustomers ? (
                      <div className="flex items-center justify-center py-4">
                        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                      </div>
                    ) : searchedCustomers.length > 0 ? (
                      <div className="space-y-1 max-h-48 overflow-y-auto">
                        {searchedCustomers.map((customer) => (
                          <button
                            key={customer.id}
                            className="w-full flex items-center gap-2 p-2 rounded-md hover:bg-muted text-left"
                            onClick={() => handleSelectCustomer(customer)}
                            data-testid={`customer-option-${customer.id}`}
                          >
                            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted text-sm font-bold">
                              {customer.name.charAt(0).toUpperCase()}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">{customer.name}</p>
                              <div className="flex items-center gap-2">
                                {customer.phone && (
                                  <p className="text-xs text-muted-foreground">{customer.phone}</p>
                                )}
                                {customer.max_discount > 0 && (
                                  <Badge variant="outline" className="text-xs text-green-600 border-green-500/30 bg-green-500/10 h-4 px-1">
                                    <Percent className="h-2.5 w-2.5 mr-0.5" />
                                    {customer.max_discount}%
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    ) : customerSearchQuery.trim() ? (
                      <p className="text-sm text-muted-foreground text-center py-2">Tidak ditemukan</p>
                    ) : null}

                    <Button 
                      variant="outline" 
                      className="w-full" 
                      onClick={() => setShowCreateCustomer(true)}
                      data-testid="create-new-customer-btn"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Buat Pelanggan Baru
                    </Button>
                  </div>
                ) : (
                  <div className="p-3 space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium">Pelanggan Baru</h4>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setShowCreateCustomer(false)}>
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="space-y-2">
                      <div>
                        <Label className="text-xs">Nama *</Label>
                        <Input
                          value={newCustomerData.name}
                          onChange={(e) => setNewCustomerData({ ...newCustomerData, name: e.target.value })}
                          placeholder="Nama pelanggan"
                          data-testid="new-customer-name"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Telepon</Label>
                        <Input
                          value={newCustomerData.phone}
                          onChange={(e) => setNewCustomerData({ ...newCustomerData, phone: e.target.value })}
                          placeholder="08xxxxxxxxxx"
                          data-testid="new-customer-phone"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Email</Label>
                        <Input
                          type="email"
                          value={newCustomerData.email}
                          onChange={(e) => setNewCustomerData({ ...newCustomerData, email: e.target.value })}
                          placeholder="Opsional"
                          data-testid="new-customer-email"
                        />
                      </div>
                    </div>
                    <Button className="w-full" onClick={handleCreateCustomer} data-testid="save-new-customer-btn">
                      Simpan & Pilih
                    </Button>
                  </div>
                )}
              </PopoverContent>
            </Popover>
          )}
        </div>

        {/* Printer Warning Banner */}
        {!canPrintReceipt && (
          <Alert variant="destructive" className="mx-4 mt-2" data-testid="printer-warning-banner">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription className="flex items-center justify-between">
              <span className="text-xs">
                {!qzConnected 
                  ? 'QZ Tray tidak terhubung' 
                  : 'Printer kasir belum dikonfigurasi'
                }
              </span>
              <Link to="/printers">
                <Button variant="outline" size="sm" className="h-6 text-xs">
                  <Printer className="h-3 w-3 mr-1" />
                  Setup
                </Button>
              </Link>
            </AlertDescription>
          </Alert>
        )}

        {/* Cart Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="font-bold flex items-center gap-2">
            <ShoppingCart className="h-5 w-5" />
            {t('pos.cart')} ({cart.length})
          </h2>
          <div className="flex items-center gap-2">
            {/* QZ Printer Status Badge */}
            <Link to="/printers" className="hover:opacity-80">
              <Badge 
                variant={qzConnected && canPrintReceipt ? 'default' : qzConnected ? 'secondary' : 'destructive'}
                className="cursor-pointer"
                data-testid="cart-printer-status"
              >
                {qzConnected ? (
                  <Printer className="h-3 w-3 mr-1" />
                ) : (
                  <AlertCircle className="h-3 w-3 mr-1" />
                )}
                {qzConnected && canPrintReceipt ? 'Ready' : qzConnected ? 'Setup' : 'Offline'}
              </Badge>
            </Link>
            {cart.length > 0 && (
              <Button variant="ghost" size="sm" onClick={clearCart} data-testid="clear-cart">
                <Trash2 className="h-4 w-4 mr-1" />
                {t('common.clear')}
              </Button>
            )}
          </div>
        </div>

        {/* Cart Items */}
        <ScrollArea className="flex-1">
          {cart.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-muted-foreground p-6">
              <ShoppingCart className="h-12 w-12 mb-2 opacity-50" />
              <p>{t('pos.cart_empty')}</p>
            </div>
          ) : (
            <div className="p-4 space-y-3">
              {cart.map((item) => (
                <div
                  key={item.id}
                  className="p-3 rounded-md bg-muted/50 space-y-2"
                  data-testid={`cart-item-${item.id}`}
                >
                  <div className="flex justify-between">
                    <div className="flex-1">
                      <p className="font-medium text-sm">{item.menu_item_name}</p>
                      {item.variant_name && (
                        <p className="text-xs text-muted-foreground">{item.variant_name}</p>
                      )}
                      {item.add_on_names?.length > 0 && (
                        <p className="text-xs text-muted-foreground">+ {item.add_on_names.join(', ')}</p>
                      )}
                      {item.notes && (
                        <p className="text-xs text-primary italic">"{item.notes}"</p>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => removeFromCart(item.id)}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => updateCartItemQty(item.id, -1)}
                      >
                        <Minus className="h-3 w-3" />
                      </Button>
                      <span className="w-8 text-center font-mono">{item.quantity}</span>
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => updateCartItemQty(item.id, 1)}
                      >
                        <Plus className="h-3 w-3" />
                      </Button>
                    </div>
                    <span className="font-mono font-medium">
                      {formatCurrency((item.unit_price * item.quantity) + item.add_on_total)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>

        {/* Cart Summary & Checkout */}
        <div className="border-t p-4 space-y-3">
          <div className="space-y-1 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t('common.subtotal')}</span>
              <span className="font-mono">{formatCurrency(subtotal)}</span>
            </div>
            {autoDiscountAmount > 0 && (
              <div className="flex justify-between text-green-600">
                <span className="flex items-center gap-1">
                  <Tag className="h-3 w-3" />
                  {t('common.discount')} {autoDiscountPercent}%
                </span>
                <span className="font-mono">-{formatCurrency(autoDiscountAmount)}</span>
              </div>
            )}
            {taxSettings.service_charge_enabled && serviceCharge > 0 && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">
                  {taxSettings.service_charge_name} ({taxSettings.service_charge_rate}%)
                </span>
                <span className="font-mono">{formatCurrency(serviceCharge)}</span>
              </div>
            )}
            {taxSettings.tax_enabled && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">
                  {taxSettings.tax_name} ({taxSettings.tax_rate}%)
                  {taxSettings.tax_inclusive && <span className="text-xs ml-1">(incl.)</span>}
                </span>
                <span className="font-mono">{formatCurrency(tax)}</span>
              </div>
            )}
            <div className="flex justify-between text-lg font-bold pt-2 border-t">
              <span>{t('common.total')}</span>
              <span className="font-mono text-primary">{formatCurrency(total)}</span>
            </div>
          </div>
          
          {/* Payment Mode Indicator & Override */}
          <div className="flex items-center justify-between p-2 rounded-md bg-muted/50">
            <div className="flex items-center gap-2">
              <Badge variant={isPrepaid ? 'default' : 'secondary'} className={isPrepaid ? 'bg-green-600' : 'bg-blue-600'}>
                {isPrepaid ? t('pos.prepaid') : t('pos.postpaid')}
              </Badge>
              <span className="text-xs text-muted-foreground">
                {isPrepaid ? t('pos.pay_first') : t('pos.prepare_first')}
              </span>
            </div>
            {paymentModeSettings.allow_override && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setPaymentModeOverride(isPrepaid ? 'POSTPAID' : 'PREPAID')}
                className="text-xs h-7"
              >
                {t('pos.switch')}
              </Button>
            )}
          </div>

          {/* Action Button - Different based on payment mode */}
          {/* PREPAID mode: Receipt will be printed after payment, so check printer */}
          {isPrepaid ? (
            <Button
              className="w-full h-12"
              onClick={handleCheckout}
              disabled={cart.length === 0 || processingOrder || !canPrintReceipt}
              data-testid="checkout-btn"
            >
              {processingOrder ? (
                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
              ) : !canPrintReceipt ? (
                <AlertCircle className="h-5 w-5 mr-2" />
              ) : (
                <CreditCard className="h-5 w-5 mr-2" />
              )}
              {!canPrintReceipt ? 'Printer Not Ready' : t('pos.pay_order')}
            </Button>
          ) : (
            /* POSTPAID mode: Order goes to kitchen first, payment later */
            <Button
              className="w-full h-12 bg-blue-600 hover:bg-blue-700"
              onClick={handleCheckout}
              disabled={cart.length === 0 || processingOrder}
              data-testid="confirm-order-btn"
            >
              {processingOrder ? (
                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
              ) : (
                <ChefHat className="h-5 w-5 mr-2" />
              )}
              {t('pos.confirm_order')}
            </Button>
          )}
        </div>
      </div>

      {/* Item Configuration Dialog */}
      <Dialog open={showItemDialog} onOpenChange={setShowItemDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{selectedItem?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Variants */}
            {selectedItem?.variants?.length > 0 && (
              <div>
                <label className="text-sm font-medium mb-2 block">Variant</label>
                <div className="flex flex-wrap gap-2">
                  {selectedItem.variants.map((variant) => (
                    <Button
                      key={variant.id}
                      variant={selectedVariant?.id === variant.id ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setSelectedVariant(variant)}
                    >
                      {variant.name}
                      {variant.price_adjustment > 0 && ` (+${formatCurrency(variant.price_adjustment)})`}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {/* Add-ons */}
            {selectedItem?.add_ons?.length > 0 && (
              <div>
                <label className="text-sm font-medium mb-2 block">Add-ons</label>
                <div className="flex flex-wrap gap-2">
                  {selectedItem.add_ons.map((addOn) => {
                    const isSelected = selectedAddOns.some(a => a.id === addOn.id);
                    return (
                      <Button
                        key={addOn.id}
                        variant={isSelected ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => {
                          if (isSelected) {
                            setSelectedAddOns(prev => prev.filter(a => a.id !== addOn.id));
                          } else {
                            setSelectedAddOns(prev => [...prev, addOn]);
                          }
                        }}
                      >
                        {addOn.name} (+{formatCurrency(addOn.price)})
                      </Button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Notes */}
            <div>
              <label className="text-sm font-medium mb-2 block">Notes</label>
              <Input
                placeholder="e.g., no chili, extra spicy..."
                value={itemNotes}
                onChange={(e) => setItemNotes(e.target.value)}
              />
            </div>

            {/* Quantity */}
            <div>
              <label className="text-sm font-medium mb-2 block">Quantity</label>
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setItemQty(Math.max(1, itemQty - 1))}
                >
                  <Minus className="h-4 w-4" />
                </Button>
                <span className="w-12 text-center font-mono text-lg">{itemQty}</span>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setItemQty(itemQty + 1)}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowItemDialog(false)}>Cancel</Button>
            <Button onClick={handleAddToCartFromDialog} data-testid="add-to-cart-dialog-btn">
              Add to Cart
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Complete Payment</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="text-center p-4 bg-muted rounded-md">
              <p className="text-sm text-muted-foreground">Total Amount</p>
              <p className="text-3xl font-bold font-mono text-primary">{formatCurrency(total)}</p>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Payment Method</label>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  variant={paymentMethod === 'CASH' ? 'default' : 'outline'}
                  onClick={() => setPaymentMethod('CASH')}
                  data-testid="payment-cash"
                >
                  <Banknote className="h-4 w-4 mr-2" />
                  Cash
                </Button>
                <Button
                  variant={paymentMethod === 'QRIS' ? 'default' : 'outline'}
                  onClick={() => setPaymentMethod('QRIS')}
                  data-testid="payment-qris"
                >
                  <QrCode className="h-4 w-4 mr-2" />
                  QRIS
                </Button>
                <Button
                  variant={paymentMethod === 'BANK_TRANSFER' ? 'default' : 'outline'}
                  onClick={() => setPaymentMethod('BANK_TRANSFER')}
                  data-testid="payment-transfer"
                >
                  <CreditCard className="h-4 w-4 mr-2" />
                  Transfer
                </Button>
                <Button
                  variant={paymentMethod === 'E_WALLET' ? 'default' : 'outline'}
                  onClick={() => setPaymentMethod('E_WALLET')}
                  data-testid="payment-ewallet"
                >
                  <Smartphone className="h-4 w-4 mr-2" />
                  E-Wallet
                </Button>
              </div>
            </div>

            {paymentMethod === 'CASH' && (
              <div>
                <label className="text-sm font-medium mb-2 block">Amount Received</label>
                <Input
                  type="number"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  className="font-mono text-lg"
                  data-testid="payment-amount"
                />
                {parseFloat(paymentAmount) >= total && (
                  <p className="text-sm text-green-600 mt-1">
                    Change: {formatCurrency(parseFloat(paymentAmount) - total)}
                  </p>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPaymentDialog(false)}>Cancel</Button>
            <Button
              onClick={processPaymentPrepaid}
              disabled={processingOrder || parseFloat(paymentAmount) < total}
              data-testid="confirm-payment-btn"
            >
              {processingOrder && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Pay & Send to Kitchen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
